import { Icon } from "@iconify/react";
import {
  Button,
  Col,
  Dropdown,
  Input,
  Menu,
  Row,
  Space,
  Table,
  ConfigProvider,
  Empty,
  Typography,
  Grid,
  TableColumnsType,
  Checkbox,
  MenuProps,
} from "antd";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { camelCase, filter, includes, isEmpty, isObject, keys, map, omit, pick, round, upperFirst } from "lodash";
import vars from "@styles/variables.module.scss";
import { useQuery } from "~/Hooks/useQuery";
import Papa from "papaparse";
import { useSearchParams } from "react-router-dom";
import { columnWidth } from "./Properties";
import styles from "./index.module.scss";
import { currencyFormat } from "~/Utils/CurrencyFormatter";
import { useMaskId } from "~/Hooks/useMaskId";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { get } from "~/Services/api.service";
import moment from "moment";
import { getFormattedDate, getKey } from "@utils/CommonUtils";
import { defaultCurrencyCode, formatToDecimal } from "~/Utils";

/** The table body never collapses below this, however short the window is. */
const MIN_TABLE_HEIGHT = 240;
/** Where the body sits before it has been rendered and can be measured. */
const DEFAULT_TABLE_TOP = 280;
/** Breathing room under the table so the last row is not flush to the edge. */
const BOTTOM_GUTTER = 32;

interface Props {
  tableData: { [key in string]: any }[];
  columns: TableColumnsType;
  tableActions?: {
    items: {
      label: string | ReactNode;
      key: string;
    }[];
    callback: (key: string, record: { [key in string]: string }) => void;
  };
  isLoading?: boolean;
  bulkDelete?: (value: React.Key[]) => void;
  selectedItems?: (value: React.Key[]) => void;
  exportFileName?: string;
  showSelection?: string;
  hideExport?: boolean;
  height?: number;
  onClick?: (record: { [key in string]: any }) => void;
  multiSelect?: boolean;
  showTopBar?: boolean;
  clearStringSearch?: boolean;
  searchQuery?: string;
  spaSearch?: any;
  clearSearch?: () => void;
  additionalCTA?: ReactNode;
  additionalCTAToRight?: boolean;
  additionalSearchCols?: any;
  excludeSearchCols?: any;
  noDataText?: string;
  singleDelete?: boolean;
  maxSelection?: number;
  expandable?: boolean;
  expandRenderer?: (values: { [key in string]: any }) => ReactNode;
  className?: string;
  size?: SizeType;
  exportOverrideColumns?: any[];
  exportOverrideDataIndexFields?: any[];
  onSearchChange?: (searchQuery: string, isLoading: boolean) => void;
  onRowExpand?: (expandedKeys: any[]) => void;
  isServerSearchApi?: boolean;
  inputDisable?: boolean;
  isPagination?: boolean;
  totalRecords?: number;
  exportApiUrl?: string;
  /**
   * Turns each record Export fetches into the row the table shows. Without it
   * the raw record is written, which only suits tables whose columns name raw
   * fields (Vendor, SOR ID); lists that map fields — division into Cost
   * Centre, a code into its label — would export those columns empty or raw.
   */
  exportRowMapper?: (record: any) => any;
  recordsPerPage?: number;
  searchPlaceholderText?: string;
  excludeExportCols?: any[];
  includeExportCols?: any[];
  tabOffset?: number;
  moduleName?: string;
  isMobile?: boolean;
  title?: string;
  isSearch?: boolean;
}

const { useBreakpoint } = Grid;
const costColumns = [
  "applicationSpend",
  "totalCost",
  "adminCost",
  "unitPrice",
  "budgets",
  "projctBudget",
  "projectCost",
  "invoiceAmount",
  "dueAmount",
  "projectBudget",
  "projectAdminCost",
  "savingsAmount",
  "annualSavingsAmount",
  "adminCostYTD",
  "adminApplicationSpend",
  "adminCostYtd",
];

const colRenderColumns = ["userLastLogin", "roles"];

const ObjColumns = [
  {
    name: "userApplications",
    value: "applicationName",
  },
  {
    name: "contracts",
    value: "contractName",
  },
  {
    name: "resourceTagsArr",
    value: "value",
  },
];

// should be used as reusable component based on the design
export const DataTable = ({
  tableData,
  columns,
  exportOverrideColumns,
  exportOverrideDataIndexFields,
  tableActions,
  isLoading,
  exportFileName,
  bulkDelete,
  selectedItems,
  totalRecords = 0,
  showSelection,
  hideExport,
  height,
  spaSearch,
  onClick,
  tabOffset = 0,
  multiSelect = true,
  showTopBar = true,
  clearSearch,
  searchQuery,
  additionalCTA = undefined,
  additionalCTAToRight,
  additionalSearchCols,
  excludeSearchCols,
  clearStringSearch,
  noDataText,
  singleDelete,
  maxSelection,
  expandRenderer,
  onRowExpand,
  expandable,
  className,
  size,
  onSearchChange,
  isServerSearchApi,
  inputDisable,
  isPagination = false,
  exportApiUrl,
  exportRowMapper,
  recordsPerPage = 0,
  searchPlaceholderText,
  excludeExportCols,
  includeExportCols,
  moduleName,
  isMobile,
  title,
  isSearch,
}: Props) => {
  const paginationOffset = isPagination ? 60 : 0;
  /* Height of the scrolling table body.

     This was measured once, in useState's initialiser, and never again — so
     the table kept whatever the window happened to be at the moment it first
     rendered. Resize the window, or open a screen at a different size, and the
     body stayed at the stale height: a short scrolling strip of rows with a
     band of empty page underneath it. It is recomputed on resize below, and
     floored so it cannot collapse on a short window. */
  const instanceClass = useRef(`dt-${Math.random().toString(36).slice(2, 9)}`).current;
  const availableHeight = useCallback(() => {
    /* Measure where the body actually starts rather than assuming it. The
       constant 280 is only right on a screen whose table sits directly under
       the page title; Approval Management adds pill tabs, a sub-heading and a
       toolbar above it, putting the body ~150px lower, so the computed height
       ran past the bottom of the window and cut the last rows and the pager
       off. Before the first paint there is nothing to measure and the old
       constant stands in; the effect below re-measures once there is. */
    const body = document.querySelector(`.${instanceClass} .ant-table-body`);
    const top = body
      ? body.getBoundingClientRect().top
      : DEFAULT_TABLE_TOP + tabOffset;
    return Math.max(
      Math.floor(window.innerHeight - top - paginationOffset - BOTTOM_GUTTER),
      MIN_TABLE_HEIGHT
    );
  }, [instanceClass, paginationOffset, tabOffset]);
  const [data, setData] = useState<Props["tableData"]>([]);
  const [filteredData, setFilteredData] = useState<Props["tableData"]>([]);
  const [searchString, setSearchString] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [tableHeight, setTableHeight] = useState(availableHeight);
  const query = useQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeExpRow, setActiveExpRow] = useState<any>([]);
  const [isExportDataLoading, setIsExportDataLoading] = useState<boolean>(false);
  const [currentPageChecked, setCurrentPageChecked] = useState<boolean>(false);
  const [currentPageIndeterminate, setCurrentPageIndeterminate] = useState<boolean>(false);
  const { unmask } = useMaskId();
  const { md, lg } = useBreakpoint();

  useEffect(() => {
    if (query.get("search")) {
      setSearchString(query.get("search") || searchString);
    }
  }, [query]);

  useEffect(() => {
    if (height) setTableHeight(height);
  }, [height]);

  /* Track the window, and re-measure whenever the table itself re-renders —
     the first pass runs before there is a body to measure. An explicit
     `height` prop still wins. */
  useEffect(() => {
    if (height) return;

    /* Settle on the same number twice and React bails out of the re-render,
       so watching the document cannot feed itself: the measurement reads the
       viewport and the body's top, neither of which the table's own height
       moves. The tolerance absorbs sub-pixel layout noise. */
    const remeasure = () =>
      setTableHeight((current) => {
        const next = availableHeight();
        return Math.abs(next - current) < 2 ? current : next;
      });

    remeasure();
    window.addEventListener("resize", remeasure);

    /* A window `resize` event is not the only way the table's box moves — a
       collapsing sidebar, or a host that resizes the viewport without firing
       one, changes it too, and the table used to keep the old height through
       both. Watching the document element catches every case. */
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(remeasure) : null;
    observer?.observe(document.documentElement);

    return () => {
      window.removeEventListener("resize", remeasure);
      observer?.disconnect();
    };
  }, [height, availableHeight, isLoading, filteredData.length]);

  useEffect(() => {
    setData(tableData);
    setFilteredData(tableData);
  }, [tableData]);

  useEffect(() => {
    if (searchQuery && !isEmpty(searchQuery)) {
      setSearchString(searchQuery);
    }
  }, [searchQuery]);

  function convertToTitleCase(inputString: any) {
    let value;
    if (inputString === "HeadofSubsidiary") {
      value = "Head of Subsidiary";
    } else if (inputString === "airportManager") {
      value = "Airport Manager";
    } else {
      value = inputString;
    }
    return value;
  }

  useEffect(() => {
    if (!isServerSearchApi) {
      if (!isEmpty(searchString)) {
        let cols = columns.map((col: any) => col.dataIndex);
        if (additionalSearchCols) {
          cols = [...cols, ...additionalSearchCols];
        }
        if (excludeSearchCols && excludeSearchCols.length > 0) {
          cols = [...cols].filter((col: any) => !excludeSearchCols.includes(col));
        }
        // keys(d)
        let result = filter(
          data,
          (d) =>
            d &&
            cols &&
            cols.some((key) => {
              return String(d[key]).toLowerCase().includes(unmask(searchString).toLowerCase());
            })
        );
        setFilteredData(result);
      } else {
        setFilteredData(data);
      }
    }
  }, [searchString, data]);

  // const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
  //   if (maxSelection && maxSelection < newSelectedRowKeys.length) {
  //     return;
  //   } else {
  //     // setSelectedRowKeys((prevState : any) => [...prevState, ...newSelectedRowKeys]);
  //     setSelectedRowKeys(newSelectedRowKeys);
  //     selectedItems && selectedItems(newSelectedRowKeys);
  //   }
  // };

  // const rowSelection = {
  //   selectedRowKeys,
  //   onChange: onSelectChange,
  // };

  const handleSelect = (record: any, selected: any) => {
    let keys = [...selectedRowKeys];
    if (selected) {
      // setSelectedRowKeys([...keys, record.key]);
      setSelectedRowKeys((keys) => [...keys, record.key]);
    } else {
      // keys = keys.filter((item) => item !== record.key);
      setSelectedRowKeys((keys) => {
        // const index = keys.indexOf(record.key);
        return keys.filter((item) => item !== record.key);
      });
    }
    // setSelectedRowKeys(keys);
  };

  const toggleSelectAll = (e: any) => {
    let keys = [...selectedRowKeys];
    let dataKeys = data.map((r) => r.key);
    if (e.target.checked) {
      let uniqueArray = [...new Set([...keys, ...dataKeys])];
      setSelectedRowKeys(uniqueArray);
      setCurrentPageChecked(true);
    } else {
      let resultArray = keys.filter((item) => !dataKeys.includes(item));
      setSelectedRowKeys(resultArray);
      setCurrentPageChecked(false);
    }
  };

  const validateCheckbox = () => {
    let keys = [...selectedRowKeys];
    let dataKeys = data.map((r) => r.key);
    function areAllElementsIncluded(array1: any, array2: any) {
      return array1.every((item: any) => array2.indexOf(item) !== -1);
    }
    function hasPartialElements(array1: any, array2: any) {
      return array1.some((item: any) => array2.indexOf(item) !== -1);
    }
    let allIncluded = keys.length > 0 ? areAllElementsIncluded(dataKeys, keys) : false;
    let partiallyIncluded = keys.length > 0 ? hasPartialElements(dataKeys, keys) : false;
    if (allIncluded) {
      setCurrentPageChecked(true);
      setCurrentPageIndeterminate(false);
    } else if (!allIncluded && partiallyIncluded) {
      setCurrentPageChecked(false);
      setCurrentPageIndeterminate(true);
    } else {
      setCurrentPageChecked(false);
      setCurrentPageIndeterminate(false);
    }
  };

  useEffect(() => {
    validateCheckbox();
    /* Report the selection upward. The prop existed but was never called —
       the only reference was commented out — so a parent could not know which
       rows were ticked, which is what "Export selected" needs. */
    selectedItems?.(selectedRowKeys);
    // eslint-disable-next-line
  }, [selectedRowKeys]);

  useEffect(() => {
    if (clearStringSearch) {
      setSearchString("");
    }
  }, [clearStringSearch]);

  useEffect(() => {
    if (data && data.length > 0) {
      validateCheckbox();
    } else {
      setCurrentPageChecked(false);
      setCurrentPageIndeterminate(false);
    }
  }, [data]);

  const headerCheckbox = (
    <Checkbox
      checked={currentPageChecked}
      indeterminate={currentPageIndeterminate}
      onChange={toggleSelectAll}
    />
  );

  const rowSelection = {
    selectedRowKeys,
    // type: "checkbox",
    fixed: true,
    onSelect: handleSelect,
    columnTitle: headerCheckbox,
    //onSelectAll: this.handleSelectAll
  };

  const getActionCol = () => {
    if (tableActions) {
      return [
        {
          /* Every Panel table labels its three-dots column "Action". */
          title: "Action",
          dataIndex: " ",
          width: 90,
          render: (value: any, record: any) => (
            <Dropdown
              overlay={
                <Menu
                  onClick={({ key, domEvent }) => {
                    /* A portalled menu bubbles up the React tree, so a row
                       click handler on the cell would fire too. */
                    domEvent.stopPropagation();
                    if (tableActions) tableActions.callback(key, record);
                  }}
                  items={tableActions.items}
                />
              }
              trigger={["click"]}
              placement="bottomLeft"
            >
              <Icon
                icon={"bi:three-dots-vertical"}
                style={{ cursor: "pointer" }}
              />
            </Dropdown>
          ),
          fixed: "right",
        },
      ] as any;
    }
    return [] as any;
  };

  const formatMsToDHM = (ms?: any): string => {
    if (!ms || ms <= 0) return "-";

    const days = Math.floor(ms / 86400000);
    ms %= 86400000;

    const hours = Math.floor(ms / 3600000);
    ms %= 3600000;

    const minutes = Math.floor(ms / 60000);

    return `${days}Days ${hours}Hrs ${minutes}Min`;
  };

  const fetchExportData = async (isExportAll: boolean) => {
    try {
      let tempData: any = [];
      let totalTimes = Math.ceil(totalRecords / recordsPerPage);
      if (totalTimes === 0) {
        return;
      }
      let currentPage = 0;
      let url = "";
      if (exportApiUrl) {
        setIsExportDataLoading(true);
        let temp1 = exportApiUrl.split("page=");
        let temp2 = temp1[1].split("&");
        temp2[0] = "{{currentPage}}";
        url = temp1[0] + "page=" + temp2.join("&");
        for (let i = 0; i < totalTimes; i++) {
          let apiUrl = url.replace("{{currentPage}}", `${currentPage}`);
          let res: any = await get(apiUrl);
          if (res && res.response && res.response.data && res.response.data.records) {
            let records = [...res.response.data.records];
            /* With a mapper, the export is exactly the table's own row — the
               formatting below is skipped, since running it over rows the
               mapper has already formatted would format dates twice. */
            if (exportRowMapper) {
              records = records.map((record: any, index: number) => ({
                ...exportRowMapper(record),
                key: getKey(currentPage, index),
              }));
            } else records.map((d: any, index: number) => {
              d.key = getKey(currentPage, index);
              if (d.amount) {
                d.amount = formatToDecimal(d.amount, d.currencyCode || defaultCurrencyCode);
              }
              if (d.authorityGroup) {
                d.authorityGroup = convertToTitleCase(d.authorityGroup);
              }
              if (d.status) {
                d.status = upperFirst(d.status);
              }
              if (d.overallStatus) {
                d.overallStatus = upperFirst(d.overallStatus);
              }
              if (d.createdon) {
                d.createdon = getFormattedDate(d.createdon);
              }
              if (d.createdOn) {
                d.createdOn = getFormattedDate(d.createdOn);
              }
              if (d.completionDate) {
                d.completionDate = getFormattedDate(d.completionDate);
              }
              if (d.completedDate) {
                d.completedDate = getFormattedDate(d.completedDate);
              }
              if (d.expiryDate) {
                d.expiryDate = getFormattedDate(d.expiryDate);
              }
              if (d.updatedOn) {
                d.updatedOn = getFormattedDate(d.updatedOn);
              }
              if (d.deactivatedOn) {
                d.deactivatedOn = getFormattedDate(d.deactivatedOn);
              }
              if (d.turnOverTimeInMilliSec) {
                d.turnOverTimeInMilliSec = formatMsToDHM(d.turnOverTimeInMilliSec);
              }
              if (d.active !== undefined && typeof d.active === "boolean") {
                d.active = d.active ? "Active" : "Inactive";
              }
              return d;
            });
            tempData = [...tempData, ...records];
          }
          currentPage += 1;
        }
        setIsExportDataLoading(false);
      } else {
        return;
      }
      formatExportData(isExportAll, tempData);
    } catch (e) {
      setIsExportDataLoading(false);
    }
  };

  const formatExportData = (isExportAll: boolean, newData: any) => {
    let displayColumns = columns.map((col: any) => {
      let dataIndex: any = col.dataIndex;
      if (exportOverrideDataIndexFields) {
        let field = exportOverrideDataIndexFields.find((d: any) => d.currentField === col.dataIndex);
        if (field) {
          dataIndex = field.newField;
        }
      }
      return {
        dataIndex: dataIndex,
        displayName: col.title,
        render: col.render && col.render,
      };
    });
    displayColumns = displayColumns.filter((col: any) => typeof col.displayName === "string");
    if (excludeExportCols) {
      displayColumns = displayColumns.filter((col: any) => !excludeExportCols.includes(col.displayName));
    }
    if (includeExportCols) {
      displayColumns = [...displayColumns, ...includeExportCols];
    }
    let tempExportData = tableData;
    if (isServerSearchApi || spaSearch) {
      tempExportData = newData;
    }

    let formattedExportData = map(
      selectedRowKeys.length > 0 && !isExportAll
        ? filter(tempExportData, (data) => selectedRowKeys.includes(data.key))
        : tempExportData,
      // tempExportData,
      (row) => {
        const modifiedRow: { [key: string]: any } = {};

        displayColumns.forEach((col) => {
          const isCurrency = costColumns.includes(col.dataIndex);
          const isObjColumn = ObjColumns.map((obj: any) => obj.name).includes(col.dataIndex);
          const filteredObjColProps = isObjColumn && ObjColumns.filter((obj: any) => obj.name === col.dataIndex)[0];

          const displayColumTitle = col.displayName;
          const dataIndexArr = col.dataIndex.split(".");

          let value: any = row;
          dataIndexArr.forEach((index: any) => {
            value = value?.[index];
          });

          // modifiedRow[displayColumTitle] = isCurrency
          //   ? currencyFormat(value, false, row.currency)
          //   : isObjColumn && Array.isArray(value)
          //   ? filteredObjColProps
          //     ? value.map((item: any) => item[filteredObjColProps.value]).join(",")
          //     : ""
          //   : value;

          let newValue = "";
          if (isCurrency) {
            if (exportOverrideColumns) {
              let newFieldRef = exportOverrideColumns.find((item: any) => item.dataIndex === col.dataIndex);
              if (newFieldRef) {
                if (newFieldRef.isAdmin) {
                  newValue = currencyFormat(row[newFieldRef.newDataIndex], true);
                } else {
                  newValue = currencyFormat(row[newFieldRef.newDataIndex], false, row.currency);
                }
              } else {
                newValue = col.render(value, row);
              }
            } else {
              newValue = col.render(value, row);
            }
          } else if (value && colRenderColumns.includes(col.dataIndex)) {
            newValue = col.render(value, row);
          } else {
            if (isObjColumn && Array.isArray(value)) {
              if (filteredObjColProps) {
                newValue = value.map((item: any) => item[filteredObjColProps.value]).join(",");
              } else {
                newValue = "";
              }
            } else if (typeof value === "boolean") {
              newValue = value ? "Active" : "Inactive";
            } else {
              newValue = value;
            }
          }
          modifiedRow[displayColumTitle] = newValue;
        });
        return modifiedRow;
      }
    );
    if (formattedExportData.length > 0) {
      const csv = Papa.unparse(formattedExportData, { quotes: true });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", exportFileName ? `${exportFileName}.csv` : "export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const customizeRenderEmpty = () => (
    <Empty
      style={{ textAlign: "center" }}
      description={noDataText}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  );

  const handleSearchChange = (value: string) => {
    setSearchString(value);
    onSearchChange && onSearchChange(value, true);
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div>
          <>
            <Button
              onClick={async () => {
                fetchExportData(true);
              }}
              style={{ width: "100%", textAlign: "left" }}
              type="text"
              disabled={totalRecords === 0}
            >
              All
            </Button>
          </>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div>
          <Button
            style={{ width: "100%", textAlign: "left" }}
            type="text"
            disabled={selectedRowKeys.length === 0}
            onClick={() => fetchExportData(false)}
          >
            Selected
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {showTopBar && (
        <Row
          gutter={16}
          // style={{ marginBottom: vars.whitespace2 }}
        >
          <Col span={24}>
            <Row>
              {title && (
                <Row
                  gutter={16}
                  style={{ marginBottom: vars.whitespace2 }}
                >
                  <Col span={24}>
                    <Typography.Title level={4}>{title}</Typography.Title>
                  </Col>
                </Row>
              )}
            </Row>
            <Row
              gutter={8}
              style={isMobile ? { gap: 10 } : undefined}
            >
              {!spaSearch && showTopBar && (
                <Col
                  xl={4}
                  lg={4}
                  md={12}
                  sm={24}
                  xs={24}
                  span={moduleName === "Logs" ? 6 : 4}
                >
                  <Input
                    title={searchPlaceholderText}
                    style={{ marginRight: 16 }}
                    disabled={inputDisable}
                    placeholder={searchPlaceholderText || "Search"}
                    value={clearStringSearch ? "" : searchString}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    suffix={
                      searchString ? (
                        <Icon
                          icon="humbleicons:times"
                          height="20"
                          onClick={() => {
                            handleSearchChange("");
                            if (clearSearch) {
                              clearSearch();
                            }
                            if (query.get("search")) {
                              searchParams.delete("search");
                              setSearchParams(searchParams);
                            }
                          }}
                          style={{
                            margin: "auto",
                            color: vars.strawberry,
                            cursor: "pointer",
                          }}
                        />
                      ) : (
                        <Icon
                          icon="eva:search-outline"
                          height="20"
                          style={{ margin: "auto", color: vars.strawberry }}
                        />
                      )
                    }
                  />
                </Col>
              )}
              {/* {additionalCTA && ( */}
              {/* From lg up the filters take whatever the search box and Export
                  leave, and Export (below) is only as wide as its button(s).
                  Export used to get a fixed 3/24 of the row, so the empty space
                  before the button grew with the screen. */}
              <Col
                xs={{ span: 24 }}
                sm={{ span: 24 }}
                md={{ span: 12 }}
                style={lg ? { flex: "1 1 0%", maxWidth: "100%" } : { maxWidth: "100%" }}
              >
                {/* Render additionalCTA on the left unless configured to the right.
                    If additionalCTAToRight is true but export is hidden, render CTA aligned right here. */}
                {additionalCTA && !additionalCTAToRight && <div>{additionalCTA}</div>}
              </Col>
              {/* )} */}
              {/* Export column. Skipped entirely when there is nothing to put in
                  it — with `hideExport` and no `additionalCTAToRight` it used to
                  render an empty 3-span Col, leaving ~126px of dead space that
                  the preceding CTA column's contents were right-aligned against. */}
              <Col
                md={12}
                sm={24}
                xs={24}
                style={
                  hideExport && !additionalCTAToRight
                    ? { display: "none" }
                    : lg
                    ? { flex: "none", maxWidth: "none" }
                    : undefined
                }
              >
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  {additionalCTAToRight ? (
                    <>
                      {!hideExport && (
                        <Dropdown
                          menu={{ items }}
                          destroyPopupOnHide={true}
                          placement="bottomRight"
                          disabled={isExportDataLoading}
                        >
                          <Button
                            type="primary"
                            ghost
                            loading={isExportDataLoading}
                          >
                            <Space>
                              <Icon
                                icon="bi:upload"
                                inline
                              />{" "}
                              Export
                            </Space>
                          </Button>
                        </Dropdown>
                      )}
                      {additionalCTA && <div>{additionalCTA}</div>}
                    </>
                  ) : (
                    <>
                      {/* additionalCTA is rendered by the dedicated CTA column
                          above when additionalCTAToRight is not set — rendering
                          it here too would duplicate the node on screen. */}
                      {!hideExport && (
                        <Dropdown
                          menu={{ items }}
                          destroyPopupOnHide={true}
                          placement="bottomRight"
                          disabled={isExportDataLoading}
                        >
                          <Button
                            type="primary"
                            ghost
                            loading={isExportDataLoading}
                          >
                            <Space>
                              <Icon
                                icon="bi:upload"
                                inline
                              />{" "}
                              Export
                            </Space>
                          </Button>
                        </Dropdown>
                      )}
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {isSearch && (
        <Row
          gutter={16}
          style={{ marginBottom: vars.whitespace2 }}
        >
          <Col span={8}>
            <Input
              title={searchPlaceholderText}
              style={{ marginRight: 16 }}
              disabled={inputDisable}
              placeholder={searchPlaceholderText || "Search"}
              value={clearStringSearch ? "" : searchString}
              onChange={(e) => handleSearchChange(e.target.value)}
              suffix={
                searchString ? (
                  <Icon
                    icon="humbleicons:times"
                    height="20"
                    onClick={() => {
                      handleSearchChange("");
                      if (clearSearch) {
                        clearSearch();
                      }
                      if (query.get("search")) {
                        searchParams.delete("search");
                        setSearchParams(searchParams);
                      }
                    }}
                    style={{
                      margin: "auto",
                      color: vars.strawberry,
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <Icon
                    icon="eva:search-outline"
                    height="20"
                    style={{ margin: "auto", color: vars.strawberry }}
                  />
                )
              }
            />
          </Col>
        </Row>
      )}
      <Col
        span={12}
        style={{
          display: "inline-flex",
          justifyContent: "end",
          marginLeft: "auto",
        }}
      >
        {showSelection && (
          <Typography.Text ellipsis>
            <span>{`${selectedRowKeys?.length} of ${showSelection}`}</span>
          </Typography.Text>
        )}
        {/* <Button>{data.length} {selectedRowKeys?.length}</Button> */}
      </Col>
      <ConfigProvider renderEmpty={noDataText ? customizeRenderEmpty : undefined}>
        <Table
          className={[className, instanceClass].filter(Boolean).join(" ")}
          loading={isLoading}
          dataSource={map(filteredData, (item, i) => ({
            ...item,
            key: item.key || i,
          }))}
          rowClassName={(record, index) => {
            if (index === filteredData.length - 1) {
              return styles.lastRow;
            }
            return "";
          }}
          columns={[
            ...columns.map((column) => {
              return {
                ...column,
                ...(column.fixed && !md && { fixed: false }),
                ...(column.width ? { width: column.width } : { width: columnWidth.GENERAL }),
                ...(onClick && {
                  onCell: (record: any) => ({ onClick: () => onClick(record) }),
                }),
                showSorterTooltip: false,
              };
            }),
            Table.EXPAND_COLUMN,
            ...getActionCol(),
          ]}
          {...(expandable &&
            expandRenderer && {
              expandable: {
                expandIcon: ({ expanded, onExpand, record }) => (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => onExpand(record, e)}
                  >
                    <Icon
                      fontSize={20}
                      icon={`${expanded ? "material-symbols:expand-less-rounded" : "material-symbols:expand-more"}`}
                    />
                  </Button>
                ),
                expandedRowRender: expandRenderer,
              },
            })}
          {...(expandable &&
            expandRenderer && {
              expandable: {
                expandedRowKeys: activeExpRow,
                onExpand: (expanded, record) => {
                  const keys = [];
                  if (expanded) {
                    keys.push(record.key);
                  }
                  setActiveExpRow(keys);
                  onRowExpand && onRowExpand(keys);
                },
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? (
                    <Button
                      onClick={(e) => onExpand(record, e)}
                      type="link"
                      style={{ padding: 0 }}
                    >
                      <Icon
                        icon="ic:baseline-keyboard-arrow-up"
                        fontSize={24}
                      />
                    </Button>
                  ) : (
                    <Button
                      onClick={(e) => onExpand(record, e)}
                      type="link"
                      style={{ padding: 0 }}
                    >
                      <Icon
                        icon="ic:outline-keyboard-arrow-down"
                        fontSize={24}
                      />
                    </Button>
                  ),
                expandedRowRender: expandRenderer,
              },
            })}
          {...(size && { size })}
          scroll={{ y: tableHeight }}
          pagination={false}
          {...(multiSelect && {
            rowSelection: rowSelection,
          })}
        />
      </ConfigProvider>
    </>
  );
};
